import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'qs';
import { Alert, Input, Spinner, Table } from 'reactstrap';

import StaffActionsService from 'services/staffActions';
import queryParser from 'helpers/queryStringParser';
import { displayUkDate, formatUkDateTime } from 'helpers/ukDate';
import { ROLE_LABELS } from 'constants/roles';
import {
    STAFF_ACTIONS,
    STAFF_ACTION_LABELS,
    STAFF_LOG_DATE_FIELDS,
    STAFF_LOG_FIELD_LABELS,
    STAFF_LOG_LIMIT,
    STAFF_LOG_ON_OFF_FIELDS,
    STAFF_LOG_WHO,
} from 'constants/staffLog';
import PaginationTableFooter from 'pages/patientManagement/tableComponents/paginationFooter';

/** The page's offset from the address bar: a whole page, never negative. */
function readOffset(query) {
    const offset = parseInt(query.offset, 10);

    return offset > 0 ? Math.floor(offset / STAFF_LOG_LIMIT) * STAFF_LOG_LIMIT : 0;
}

/** The action filter from the address bar: one the page knows, or none. */
function readAction(query) {
    return STAFF_ACTIONS.includes(query.action) ? query.action : '';
}

/** The "Who" filter from the address bar (?actorRole=): a role the page offers, or none (everyone). */
function readWho(query) {
    return STAFF_LOG_WHO.find(option => String(option.role) === query.actorRole) || null;
}

/** What an empty list says, for the filters chosen. */
function emptyLabel(action, who) {
    return `No staff changes${action ? ' of this kind' : ''}${who ? ` by ${who.inSentence}` : ''} yet`;
}

const actionLabel = action => STAFF_ACTION_LABELS[action] || action;

const roleLabel = role => ROLE_LABELS[role] || `Role ${role}`;

const fieldLabel = field => STAFF_LOG_FIELD_LABELS[field] || field;

/** Who did it: their name now, or their account number when the account no longer exists. */
function actorLabel(actor) {
    if (actor && actor.name) {
        return actor.name;
    }

    return actor && actor.id ? `Account #${actor.id} (no longer exists)` : 'Unknown';
}

/** What it was done to. The server names staff accounts, codes and sessions only - never a patient. */
function targetLabel(target) {
    if (!target) {
        return '';
    }

    switch (target.type) {
        case 'user':
            return target.name || `Account #${target.id}`;
        case 'appointmentSession':
            return target.name ? `Session #${target.id}, ${displayUkDate(target.name)}` : `Session #${target.id}`;
        case 'activationCode':
            return target.name ? `Code ${target.name}` : `Code #${target.id} (deleted)`;
        default:
            return `${target.type} #${target.id}`;
    }
}

/** One recorded value, as staff read it. Nothing is masked: these are staff fields. */
function valueLabel(field, value) {
    if (value === null || value === undefined || value === '') {
        // An activation code with no limit is unlimited, not "nothing".
        return field === 'numberOfUses' ? 'Unlimited' : '(none)';
    }
    if (typeof value === 'string' && STAFF_LOG_DATE_FIELDS.includes(field)) {
        return displayUkDate(value) || value;
    }
    if (typeof value === 'boolean') {
        if (STAFF_LOG_ON_OFF_FIELDS.includes(field)) {
            return value ? 'On' : 'Off';
        }
        return value ? 'Yes' : 'No';
    }

    return String(value);
}

/**
 * Log history: the staff log - every change staff made to a clinician's account, and every use of the
 * session and activation-code tools, newest first. Super admins only: the menu and the route keep
 * everyone else out, and the API refuses them (403).
 *
 * The page and the two filters - what (?action=) and who (?actorRole=: Admins, super admins or Doctors,
 * by their role at the time) - live in the address bar (?offset=&action=&actorRole=), so Back and
 * Forward work and a filtered page can be shared.
 */
export default function StaffLog() {
    const history = useHistory();
    const location = useLocation();
    const query = queryParser(location.search);
    const offset = readOffset(query);
    const action = readAction(query);
    const who = readWho(query);
    const actorRole = who ? who.role : null;

    // 'loading' | 'loaded' | 'failed'
    const [status, setStatus] = useState('loading');
    const [entries, setEntries] = useState([]);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        // An answer for a page or filter the user has already left is dropped.
        let current = true;
        const params = { limit: STAFF_LOG_LIMIT, offset };

        if (action) {
            params.action = action;
        }
        if (actorRole) {
            params.actorRole = actorRole;
        }

        setStatus('loading');
        StaffActionsService.getStaffActions(params).then((response) => {
            if (!current) {
                return;
            }
            if (!response || !Array.isArray(response.data)) {
                setStatus('failed');
                return;
            }
            setEntries(response.data);
            setTotal(Number(response.pagination && response.pagination.totalCount) || 0);
            setStatus('loaded');
        }, () => {
            if (current) {
                setStatus('failed');
            }
        });

        return () => {
            current = false;
        };
    }, [offset, action, actorRole]);

    const goTo = (changes) => {
        const next = { ...query, ...changes };

        Object.keys(next).forEach((key) => {
            if (next[key] === undefined || next[key] === '' || next[key] === 0) {
                delete next[key];
            }
        });
        history.push({ search: qs.stringify(next) });
    };

    // The pager also calls this once when it appears (for the page it starts on): that is not a move.
    const handleChangePage = ({ selected }) => {
        const next = selected * STAFF_LOG_LIMIT;

        if (!isNaN(next) && next !== offset) {
            goTo({ offset: next });
        }
    };

    const handleChangeAction = (event) => {
        goTo({ action: event.target.value, offset: undefined });
    };

    const handleChangeWho = (event) => {
        goTo({ actorRole: event.target.value, offset: undefined });
    };

    return (
        <div className="page-content mx-2 pb-2">
            <div className="card pr-20px pl-20px pt-20px pb-20px" data-testid="staff-log-page">
                <label className="details-caption">Staff changes</label>
                <p className="text-muted font-size-12 mb-3">
                    Admin changes to doctors&apos; accounts and every use of the session and activation-code tools.
                    Newest first; times are UK time. Role changes are under History on the Clinicians page.
                </p>

                <div className="d-flex flex-wrap align-items-center mb-2">
                    <div className="d-flex align-items-center mr-4 mb-2">
                        <label htmlFor="staff-log-filter" className="mb-0 mr-2 text-nowrap">Show</label>
                        <Input
                            type="select"
                            id="staff-log-filter"
                            data-testid="staff-log-filter"
                            value={action}
                            onChange={handleChangeAction}
                            style={{ maxWidth: 360 }}
                        >
                            <option value="">All changes</option>
                            {STAFF_ACTIONS.map(key => (
                                <option key={key} value={key}>{STAFF_ACTION_LABELS[key]}</option>
                            ))}
                        </Input>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                        <label htmlFor="staff-log-who" className="mb-0 mr-2 text-nowrap">Who</label>
                        <Input
                            type="select"
                            id="staff-log-who"
                            data-testid="staff-log-who"
                            value={who ? String(who.role) : ''}
                            onChange={handleChangeWho}
                            style={{ maxWidth: 200 }}
                        >
                            <option value="">Everyone</option>
                            {STAFF_LOG_WHO.map(option => (
                                <option key={option.role} value={String(option.role)}>{option.label}</option>
                            ))}
                        </Input>
                    </div>
                </div>

                {status === 'loading' ? (
                    <div className="d-flex justify-content-center py-4" data-testid="staff-log-loading">
                        <Spinner color="primary" />
                    </div>
                ) : null}

                {status === 'failed' ? (
                    <Alert color="warning" data-testid="staff-log-notice">
                        Could not load the staff log. Please try again.
                    </Alert>
                ) : null}

                {status === 'loaded' && total === 0 ? (
                    <p className="text-muted py-4 text-center mb-0" data-testid="staff-log-empty">
                        {emptyLabel(action, who)}
                    </p>
                ) : null}

                {status === 'loaded' && total > 0 && entries.length === 0 ? (
                    <p className="text-muted py-4 text-center mb-0" data-testid="staff-log-empty-page">
                        Nothing on this page.
                    </p>
                ) : null}

                {status === 'loaded' && entries.length > 0 ? (
                    <div className="table-responsive">
                        <Table className="table mb-0" data-testid="staff-log-table">
                            <thead>
                                <tr>
                                    <th>When</th>
                                    <th>Who</th>
                                    <th>What</th>
                                    <th>Changes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map(entry => (
                                    <tr key={entry.id} data-testid={`staff-log-row-${entry.id}`}>
                                        <td className="text-nowrap">{formatUkDateTime(entry.createdAt)}</td>
                                        <td>
                                            <div>{actorLabel(entry.actor)}</div>
                                            <div className="text-muted font-size-12">{roleLabel(entry.actor && entry.actor.role)}</div>
                                        </td>
                                        <td>
                                            <div>{actionLabel(entry.action)}</div>
                                            <div className="text-muted font-size-12">{targetLabel(entry.target)}</div>
                                        </td>
                                        <td>
                                            <ul className="list-unstyled mb-0">
                                                {(entry.changes || []).map(change => (
                                                    <li key={change.field} data-testid={`staff-log-change-${entry.id}-${change.field}`}>
                                                        {`${fieldLabel(change.field)}: ${valueLabel(change.field, change.from)} → ${valueLabel(change.field, change.to)}`}
                                                    </li>
                                                ))}
                                            </ul>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                ) : null}

                {status === 'loaded' && total > STAFF_LOG_LIMIT ? (
                    <section className="mt-4" data-testid="staff-log-pages">
                        <PaginationTableFooter
                            key={`${action}|${actorRole || ''}|${offset}`}
                            total={total}
                            handleChangePage={handleChangePage}
                            currentOffset={offset}
                            limit={STAFF_LOG_LIMIT}
                        />
                    </section>
                ) : null}
            </div>
        </div>
    );
}
